<template>
  <div class="px-8 pr-8 pt-4 lg:p-12 pb-4">
    <ProductDescription
      :title="product?.headline"
      :description="product?.shortDescription"
    />
  </div>
  <section class="px-8 lg:px-12" aria-label="Product Price">
    <div class="mb-[1.4285714286rem]" v-if="formattedPrice !== 'N/A'">
      <span class="text-[1.7142857143rem] font-medium text-ash-200">{{ formattedPrice }}</span>
      <span class="text-4 text-ash-100" v-if="product?.showUnitCount && product?.totalUnitCount"> /{{ product?.totalUnitCount + " " + product?.recommendedDosage?.unit }}</span>
      <div v-if="isOGenabled && ordergrooveConfigResolved && product?.allowSubscriptions && !product?.loyaltyOnly && !isProfessional && subscriptionPrice !== 'N/A' && product?.discountable" class="text-sm leading-[19.6px] text-gray-700">
          <span>or </span>
          <span class="font-bold text-[#2F8B68]">{{ subscriptionPrice }}</span>
          <span> with subscription (10% off)</span>
      </div>
    </div>
    <div class="unavailable-in-locale mb-[1.4285714286rem]" v-else>
      <Button id="mobile-filter" type="button" theme="DARK" size="md" :disabled="true"
        class="font-bold text-[1rem] py-[0.429rem] text-gray-600! h-[3.5714285714rem] cursor-not-allowed relative rounded-lg gap-1 opacity-50 w-full px-4 bg-gray-300! focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
        aria-label="Unavailable in your country" title="Unavailable in your country">
        Unavailable in your country
      </Button>
    </div>
  </section>
  <section class="px-8 pr-8 lg:px-12 lg:pr-0">
    <ProductVariant
      :productFamily="product?.productFamily"
      :activeProductId="activeProductId"
      @select="onVariantSelect"
      class="mb-[1.4285714286rem]"
    />
  </section>
  <section class="px-8 pr-8 lg:px-12 lg:pr-0">
    <Bundle v-if="product?.productsInBundle?.length"
      :products-in-bundle="product?.productsInBundle ?? []"
      @select="onVariantSelect" 
    />
  </section>
   <div>
  <div v-if="ordergrooveConfigResolved && !isOGenabled && product?.allowSubscriptions && !product?.loyaltyOnly" class="border-b border-gray-200 md:px-12 md:pr-0 px-8 lg:pb-0 pt-0">
    <div class="subscribe">
      <div class="subscribe-cta" role="button" tabindex="0" id="subscribe-cta-button" aria-label="Toggle subscription"
        :aria-expanded="showSubscriptions ? 'true' : 'false'"
        @click="toggleSubscriptions" @keydown.enter.space.prevent="toggleSubscriptions">
        <Icon icon='badge-dollar' packPrefix="fas" />
        <span class="cta-text">
          Subscribe: <strong>SAVE UP TO 20%</strong>
        </span>
        <Icon :icon="showSubscriptions ? 'chevron-up' : 'chevron-down'" />
      </div>

      <div v-if="showSubscriptions" class="subscription-dropdown">
        <Subscription v-model="frequency" />

        <div class="subscription-benefits">
          <span v-if="product?.discountable" class="benefit">
            SAVE 10% on each refill order.
          </span>
          <span v-else class="benefit">
            This product does not qualify for discount.
          </span>

          <ul>
            <li>
              <Icon icon="check" class="mr-2" /> Save 10% on each order
            </li>
            <li>
              <Icon icon="check" class="mr-2" /> Cancel or modify anytime
            </li>
            <li>
              <Icon icon="check" class="mr-2" /> Free shipping
            </li>
          </ul>
        </div>

        <div class="subscription-benefits">
          <span v-if="product?.discountable" class="benefit">
            SAVE 20% when you subscribe to 3 or more products.
          </span>

          <Tooltip :placement="'top'">
            <template #trigger>
              <Icon icon='info-circle' packPrefix="fas" />
            </template>
            <div class="tooltip-list">
              <ul>
                <li>Save 10% on subscription refills when you subscribe to 1 or 2 different Thorne products.</li>
                <li>Save 20% on subscription refills when you subscribe to 3 or more different Thorne products.</li>
                <li>Your subscription discount will be automatically adjusted based on your number of current
                  subscriptions.</li>
              </ul>
            </div>
          </Tooltip>
        </div>

        <div class="restrictions text-ash-100">
          Subscription discounts are exclusive to retail customers and
          <span class="text-red-100">
            the discount does not apply to product bundles.
          </span>
          <Tooltip :placement="'top'">
            <template #trigger>
              <Icon icon='info-circle' packPrefix="fas" />
            </template>
            <div class="tooltip-list">
              <ul>
                <li>Wholesale subscriptions are not eligible to receive 10% off and will not receive free shipping on
                  the
                  initial product purchase.</li>
                <li>Free shipping is only available to orders placed within the Contiguous United States.</li>
              </ul>
            </div>
          </Tooltip>
        </div>
      </div>
    </div>

    <div v-if="product?.restrictedLocations?.length" class="mt-4 text-sm text-gray-700">
      <p class="flex items-center gap-2 font-medium">
        <span>
          Shipping restrictions may apply to international destinations.
        </span>
        <Icon icon='info-circle' packPrefix="fas" class="cursor-pointer" @mouseenter="showRestrictionTooltip = true"
          @mouseleave="showRestrictionTooltip = false" />
      </p>

      <div v-if="showRestrictionTooltip"
        class="mt-2 rounded-md border border-gray-200 bg-white p-4 shadow-md text-center">
        <p class="mb-2 text-ash-100">
          This product is not permitted for distribution in:
        </p>

        <ul class="space-y-1 list-none pl-0">
          <li v-for="loc in product?.restrictedLocations" :key="loc" class="">
            {{ loc }}
          </li>
        </ul>
      </div>

    </div>
  </div>
  <div v-if="ordergrooveConfigResolved && isOGenabled && product?.allowSubscriptions && !product?.loyaltyOnly" class="px-8 md:px-12 py-4 border-b border-gray-200 md:pr-0 lg:pb-4 pt-0">
    <og-offer ref="ogProductOffer" location="PDP"></og-offer>
    </div>
    <AddToCartPdp :product-id="activeProductId" :product="product" :frequency="frequency" :add-to-cart-id="addToCartId" :og-offer-element="ogProductOffer"  og-offer-location="PDP"/>
  </div>
  <div class="lg:pr-0 md:pr-0 px-8 md:px-12 pt-1" v-if="showTrueMed || isOGenabled">
    <div class="fsa-hsa-indicator">
      <div id="truemed-instructions" icon-height="12" data-public-id="tm_qual_85m6tl1tun"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, toRef, watch } from 'vue'
import Subscription from '../../organism/product/sideBar/subscription.vue'
import Tooltip from '../../organism/product/sideBar/tooltip.vue'
import Icon from '../../atom/icon.vue';
import Bundle from '../../organism/product/sideBar/bundle.vue'
import AddToCartPdp from '../../organism/product/sideBar/addToCartPDP.vue'
import ProductVariant from '../../organism/product/sideBar/productVariant.vue'
import ProductDescription from '../../organism/product/sideBar/descriptionSection.vue'
import type { Article } from '~~/shared/types/common'
import Button from '../../atom/button.vue'
import {useDynamicYieldStore} from '~/stores/dynamicYieldStore'
import {useLocaleStore} from '~/stores/localeStore'
import { useAppConfigurationStore } from '~/stores/appConfigurationStore'
import { useOrderGrooveSelectFix } from '~/composables/useOrderGrooveSelectFix'

const dyStore = useDynamicYieldStore();
const { dynamicYieldTruemedData } = storeToRefs(dyStore);
const appConfigurationStore = useAppConfigurationStore()

const showSubscriptions = ref(false)
const frequency = ref(0)
const showRestrictionTooltip = ref(false)
const ogProductOffer = ref<HTMLElement | null>(null);
const ordergrooveEnabled = ref(false)
const ordergrooveConfigResolved = ref(false)
const props = defineProps<{
  unavailableInLocale?: boolean,
  blogContent: Article[],
  product: Product | undefined
  thorneVerifiedData?: { label: string; link: string; icon: string }[]
  activeProductId: number
  addToCartId: string
}>();

const product = computed( () => props.product)
const isProfessional = computed(() => {
  const localeStore = useLocaleStore()
  return localeStore.isProfessional
})

const price = computed(() => {
  const rawPrice = product.value?.salePrice ?? product.value?.retailPrice ?? product.value?.price

  if (typeof rawPrice === 'number') {
    return Number.isFinite(rawPrice) ? rawPrice : null
  }

  if (typeof rawPrice === 'string') {
    const parsed = Number(rawPrice.replace(/[^0-9.-]+/g, ''))
    return Number.isFinite(parsed) ? parsed : null
  }

  return null
})

const formatDisplayPrice = (amount: number, currency: string) => {
  return currency.concat(amount.toFixed(2).replace('.00', ''))
}

const formattedPrice = computed(() => {
  if (product.value?.loyaltyOnly) {
    return `${product.value.loyaltyPointCost} points`
  }
  if (!price.value) return 'N/A'
  const currency: string = product.value?.priceCurrency ?? '$'
  return formatDisplayPrice(price.value, currency)
})

const subscriptionPrice = computed(() => {
  const basePrice = Number(price.value)
  if (!Number.isFinite(basePrice)) return 'N/A'
  const finalPrice = Math.round((basePrice * 0.90 + Number.EPSILON) * 100) / 100
  const currency: string = product.value?.priceCurrency ?? '$'
  return formatDisplayPrice(finalPrice, currency)
})
const emit = defineEmits(['swappedProduct'])
const swapRelatedProduct = (id) => {
  emit('swappedProduct', id)
}

const toggleSubscriptions = () => {
  showSubscriptions.value = !showSubscriptions.value
}

const onVariantSelect = (
  variant: {
    productId: number | string
    productName: string
    productUrl: string
  }
) => {
  if (!URL.parse(variant.productUrl)) return
  const relativeProductURL = URL.parse(variant.productUrl)?.pathname
  if(product.value) swapRelatedProduct(product.value.sku)
  navigateTo(relativeProductURL, { external: true })
}

const isSubscriptionSelected = computed(() => {
  return frequency.value > 0
})

const toBoolean = (val: unknown) => {
  return typeof val === 'string'
    ? val.toLowerCase() === 'true'
    : !!val
}

const isOGenabled = computed(() => {
  return ordergrooveEnabled.value
})

const syncOrdergrooveConfig = async () => {
  try {
    const response = await appConfigurationStore.fetchAppConfigurations()
    ordergrooveEnabled.value = toBoolean(response?.ordergrooveEnabled)
  } catch (error) {
    ordergrooveEnabled.value = false
  } finally {
    ordergrooveConfigResolved.value = true
  }
}

useOrderGrooveSelectFix(isOGenabled)

onMounted(() => {
  syncOrdergrooveConfig()
  syncOgOffer()
})

const syncOgOffer = async () => {
  
  if (!props.product?.allowSubscriptions  || props.product?.loyaltyOnly) return

  await nextTick()

  const offerEl = ogProductOffer.value
  if (!offerEl) return

  const productId = props.product?.id ?? props.activeProductId
  offerEl.setAttribute('product', productId?.toString() ?? 'unknown')
}

watch(
  () => [props.activeProductId, props.product?.id, isOGenabled.value],
  syncOgOffer,
  { flush: 'post' }
)

const stringToBoolean  = (val) => {
  return toBoolean(val)
}

const showTrueMed = computed(() => {
  const data = dynamicYieldTruemedData.value;
  if (!data) return false;

  return props.product?.hsaFsaAccepted &&
      stringToBoolean(data.enabled) &&
      (!isSubscriptionSelected.value || stringToBoolean(data.subscription));
});

</script>
